import React, { FC } from 'react';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import { twMerge } from 'tailwind-merge';

interface PaginatorProps {
  totalItems: number;
  itemsPerPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  showArrows?: boolean;
}

const Paginator: FC<PaginatorProps> = ({ totalItems, itemsPerPage, currentPage, onPageChange, showArrows = false }) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const generatePageNumbers = () => {
    const pages: (number | string)[] = [];
    const range = 1; // Adjust this range as needed

    // Calculate start and end page numbers to display
    let startPage = Math.max(2, currentPage - range);
    let endPage = Math.min(totalPages - 1, currentPage + range);

    // Ensure constant number of visible items (5 including first and last page)
    if (currentPage <= range + 2) {
      endPage = Math.min(totalPages - 1, range + 3);
    } else if (currentPage >= totalPages - range - 1) {
      startPage = Math.max(2, totalPages - range - 3);
    }

    pages.push(1);

    if (startPage > 2) {
      pages.push('...');
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages - 1) {
      pages.push('...');
    }

    pages.push(totalPages);

    return pages;
  };

  const handlePageClick = (page: number | string) => {
    if (typeof page === 'number' && page !== currentPage) {
      onPageChange(page);
    }
  };

  return (
    <div className="flex items-center justify-start space-x-2">
      {showArrows && (
        <IoChevronBack
          className={twMerge('text-ph-gray-jumbo', currentPage > 1 && 'cursor-pointer text-ph-purple')}
          onClick={() => {
            if (currentPage > 1) onPageChange(currentPage - 1);
          }}
        />
      )}

      {generatePageNumbers().map((page, index) => (
        <button
          key={index}
          className={twMerge(
            'rounded-lg px-3 py-1',
            page === currentPage
              ? 'border-ph-purple bg-ph-purple font-bold text-white'
              : 'border-gray-300 text-gray-700',
            page === '...' && 'cursor-default',
          )}
          onClick={() => handlePageClick(page)}
          disabled={page === '...' || page === currentPage}
        >
          {page}
        </button>
      ))}
      {showArrows && (
        <IoChevronForward
          className={twMerge('text-ph-gray-jumbo', currentPage < totalPages && 'cursor-pointer text-ph-purple')}
          onClick={() => {
            if (currentPage < totalPages) onPageChange(currentPage + 1);
          }}
        />
      )}
    </div>
  );
};

export default Paginator;
